import React, { useState } from "react";
import { Box, Button, HStack, IconButton, Link, Stack, Text, useToast, SimpleGrid } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { BasicInput, CertificateUpload, SelectInput } from "./HookFormsInputs";
import MoblyzeApolloClient from "../services/MoblyzeApolloClient";
import { CANDIDATE_CERTIFICATE_DELETE_MUTATION, CANDIDATE_CERTIFICATE_UPLOAD_MUTATION, GET_CERTIFICATIONS_TYPE } from "../graphql/jobs";
import { useQuery } from "@apollo/client";
import moment from "moment";
import { DeleteIcon } from "@chakra-ui/icons";

const AddCertificatesForm = ({
  candidateUser,
  refetch,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const { data } = useQuery(GET_CERTIFICATIONS_TYPE);

  const certificationTypes = data?.getCertifications?.map((type) => ({ value: type.id, label: type.name }));
  
  const {
    handleSubmit,
    register,
    control,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
  });

  const onFormSubmit = async () => {
    try {
      setIsSubmitting(true);
      const { type, certificates, expiryDate, issueDate} = getValues()

      if (!certificates || certificates.length === 0) {
        toast({
          title: "Please upload at least one certificate.",
          status: "warning",
        });
        return;
      }

      // Sequentially upload each certificate
      for (const certificate of certificates) {
        await MoblyzeApolloClient.mutate({
          mutation: CANDIDATE_CERTIFICATE_UPLOAD_MUTATION,
          variables: {
            candidateProfileId: candidateUser?.candidateProfileId,
            file: certificate,
            name: type.label,
            expiry: moment(expiryDate).format(),
            issue: moment(issueDate).format(),
          },
        });
      }

      if (refetch) {
        refetch();
      }

      toast({
        title: "Certificates have been added.",
        status: "success",
      });

      reset({
        type: null,
        certificates: null,
        expiryDate: null,
        issueDate: null
      });
    } catch (e) {
      console.error('Error occurred while submitting the form:', e);
      toast({
        title: e?.message || JSON.stringify(e),
        status: "error",
      });
    } finally {
      setIsSubmitting(false)
    }
  };

  const certifications = candidateUser?.candidateProfile?.certification || [];

  const handleDeleteCertificate = async (id) => {

    try {
      await MoblyzeApolloClient.mutate({
        mutation: CANDIDATE_CERTIFICATE_DELETE_MUTATION,
        variables: { id, candidateProfileId: candidateUser?.candidateProfileId },
      });
      toast({
        title: "Certificate deleted successfully.",
        status: "success",
      });
      if (refetch) refetch();
    } catch (error) {
      toast({
        title: error?.message || "Failed to delete certificate.",
        status: "error",
      });
    }
  };
  
  return (
    <>
      <Box borderRadius="5px" background="gray.800" p="44px 20px" width="100%">
        <Text
          color={"white"}
          fontSize={"28px"}
          fontWeight={600}
          lineHeight={"40px"}
        >
          Add Certification
        </Text>
        <Stack spacing={4} mt="8" width="100%">
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <SelectInput
              name="type"
              label="Type"
              placeholder="Select type"
              required
              options={certificationTypes}
              register={register}
              control={control}
              errors={errors}
              ml={"0"}
              mb={"32px"}
            />

            <BasicInput
              name="issueDate"
              label="Issue Date"
              placeholder="Issue Date"
              required
              typeOverride="date"
              register={register}
              control={control}
              errors={errors}
              ml={"0"}
              mb={"32px"}
            />
            <BasicInput
              name="expiryDate"
              label="Expiry Date"
              placeholder="Expiry Date"
              // required
              typeOverride="date"
              register={register}
              control={control}
              errors={errors}
              ml={"0"}
              mb={"32px"}
            />
            <CertificateUpload
              name="certificates"
              label="Certificate"
              placeholder="Select certificate"
              required={true}
              multiple={true}
              register={register}
              control={control}
              errors={errors}
              ml={"0"}
              mb={"32px"}
            />
            <Button
              width="100%"
              type="submit"
              isDisabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
              loadingText="Saving..."
            >
              Save
            </Button>
          </form>
        </Stack>
      </Box>

      {/* Certification List */}
      {certifications?.length > 0 && (
  <Box>
    <Text fontSize="20px" fontWeight="semibold" mb={4}>
      Existing Certificates
    </Text>
    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
      {certifications.map((cert) => (
        <Box
          key={cert.id}
          p={4}
          borderRadius="md"
          bg="gray.100"
          color="black"
        >
          <HStack justify="space-between" align="start">
            <Box justify="space-between">
              <Text fontWeight="bold">{cert.name}</Text>
              <Link href={cert.url} isExternal color="blue.500">
                View Certificate
              </Link>
            </Box>
            <IconButton
              aria-label="Delete certificate"
              icon={<DeleteIcon />}
              colorScheme="red"
              variant="ghost"
              onClick={() => handleDeleteCertificate(cert.id)}
            />
          </HStack>
        </Box>
      ))}
    </SimpleGrid>
  </Box>
)}

    </>
  );
};

export default AddCertificatesForm;
